<template>
  <div :id="`${source}_routing_funnel_graph`">
    <ContainerInfoItem
      :source="`${source}_routing_funnel_graph`"
      title="Routing Funnel"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <BarChart
        v-else-if="chartCategories && chartData"
        :id="`${source}_routing_funnel_chart`"
        :categories="chartCategories"
        :series-data="chartData"
        :alignment="chartAlignment.VERTICAL"
        primary-color="#A146E8"
      />

      <EmptyScreen
        v-else
        :source="`${source}_routing_funnel_graph`"
        text="No routing data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { chartAlignment } from "@/src/constants/chartConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import AnalyticService from "@/src/api/AnalyticService.js";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const processRoutingFunnelData = (data) => {
  if (!data) {
    return { categories: null, seriesData: null };
  }

  const categories = ["Total form submissions", "Total meetings booked"];

  const totalFormSubmissions = data.total_response || 0;
  const totalMeetingsBooked = data.meeting_booked || 0;

  if (totalFormSubmissions === 0 && totalMeetingsBooked === 0) {
    return { categories: null, seriesData: null };
  }

  const routingValues = [totalFormSubmissions, totalMeetingsBooked];

  const seriesData = [
    {
      name: "Routing Conversion",
      data: routingValues,
      color: "#A146E8",
    },
  ];

  return { categories, seriesData };
};

const toFetchRoutingFormFunnel = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      routing_form_id: filters.value?.routing_form?.id,
    };

    const response = await AnalyticService.V2.fetchRoutingFormFunnel(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;
      const { categories, seriesData } = processRoutingFunnelData(data);

      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching routing form funnel: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value) toFetchRoutingFormFunnel();
});

useAnalyticsRefresh(toFetchRoutingFormFunnel, [range]);

onMounted(() => {
  toFetchRoutingFormFunnel();
});
</script>
